import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, PieChart } from "@mantine/charts";
import {
  Box,
  Center,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconFirstAidKit, IconMoneybag, IconUsers } from "@tabler/icons-react";

import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TruncatedCell } from "@components/Table/TruncatedCell";

import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import {
  consultationsTrendSeries,
  labsTrendSeries,
  prescriptionsTrendSeries,
  topPerformingStaffSeries,
} from "@data/series";

import { ROLES } from "@constants/index";

import { useGetConsultationsTrend } from "@hooks/queries/dashboard/useGetConsultationsTrend";
import { useGetFrequentServices } from "@hooks/queries/dashboard/useGetFrequentServices";
import { useGetLabsTrend } from "@hooks/queries/dashboard/useGetLabsTrend";
import { useGetPrescriptionsTrend } from "@hooks/queries/dashboard/useGetPrescriptionsTrend";
import { useGetTopPerformingStaff } from "@hooks/queries/dashboard/useGetTopPerformingStaff";
import { useGetTotalBillsStatuses } from "@hooks/queries/dashboard/useGetTotalBillsStatuses";
import { useGetTotalConsultations } from "@hooks/queries/dashboard/useGetTotalConsultations";
import { useGetTotalEarnings } from "@hooks/queries/dashboard/useGetTotalEarnings";
import { useGetTotalPatients } from "@hooks/queries/dashboard/useGetTotalPatients";

import { DateRangeSelect } from "../components/DateRangeSelect";


export const Overview = () => {
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [topPerformingStaffType, setTopPerformingStaffType] = useState<
    string | null
  >("doctor");
  const [topPerformingStaffRecord, setTopPerformingStaffRecord] = useState<
    string | null
  >("all");
  const [trendTarget, setTrendTarget] = useState<number | string>();
  const [trendsFilter, setTrendsFilter] = useState<string | null>(
    "consultations"
  );
  const [trendData, setTrendData] = useState<any>([]);
  const [trendSeries, setTrendSeries] = useState<
    { name: string; color: string }[]
  >(consultationsTrendSeries);

  const {
    data: consultations,
    isLoading: isConsultationsLoading,
    isError: isConsultationsError,
    isSuccess: isConsultationsSuccess,
  } = useGetTotalConsultations({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });

  const {
    data: patients,
    isLoading: isPatientsLoading,
    isError: isPatientsError,
    isSuccess: isPatientsSuccess,
  } = useGetTotalPatients({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: bills,
    isLoading: isBillsLoading,
    isError: isBillsError,
    isSuccess: isBillsSuccess,
  } = useGetTotalBillsStatuses({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: earnings,
    isLoading: isEarningsLoading,
    isError: isEarningsError,
    isSuccess: isEarningsSuccess,
  } = useGetTotalEarnings({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: frequentServices,
    isLoading: isFrequentServicesLoading,
    isError: isFrequentServicesError,
    isSuccess: isFrequentServicesSuccess,
  } = useGetFrequentServices({
    dateRange: dateRange as string,
    limit: 10,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: topPerformingStaff,
    isLoading: isTopPerformingStaffLoading,
    isError: isTopPerformingStaffError,
    isSuccess: isTopPerformingStaffSuccess,
  } = useGetTopPerformingStaff({
    dateRange: dateRange as string,
    limit: 5,
    staffType: topPerformingStaffType as string,
    recordType: topPerformingStaffRecord as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: consultationsTrend,
    isLoading: isConsultationsTrendLoading,
    isError: isConsultationsTrendError,
    isSuccess: isConsultationsTrendSuccess,
  } = useGetConsultationsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: labsTrend,
    isLoading: isLabsTrendLoading,
    isError: isLabsTrendError,
    isSuccess: isLabsTrendSuccess,
  } = useGetLabsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: prescriptionsTrend,
    isLoading: isPrescriptionsTrendLoading,
    isError: isPrescriptionsTrendError,
    isSuccess: isPrescriptionsTrendSuccess,
  } = useGetPrescriptionsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });

  const rows = useMemo(() => {
    return frequentServices?.map((item, index) => (
      <Table.Tr key={index} h={40}>
        <Table.Td fw={500} ff={"var(--font-secondary)"}>
          {item.frequency}
        </Table.Td>
        <Table.Td>
          <TruncatedCell value={item.service} />
        </Table.Td>
        <Table.Td ta={"right"} ff={"var(--font-secondary)"}>
          {item.earnings.toLocaleString(i18n.language)}
        </Table.Td>
      </Table.Tr>
    ));
  }, [frequentServices, i18n.language]);

  const pieChartData = useMemo(
    () => [
      { name: "Paid", value: bills?.paid || 0, color: "teal.5" },
      { name: "Accepted", value: bills?.accepted || 0, color: "indigo.5" },
      { name: "Pending", value: bills?.pending || 0, color: "orange.5" },
    ],
    [bills]
  );

  const filteredSeries =
    topPerformingStaffRecord === "all"
      ? topPerformingStaffSeries
      : topPerformingStaffSeries.filter(
          item => item.name === topPerformingStaffRecord + "s"
        );

  useEffect(() => {
    if (trendsFilter) {
      if (trendsFilter === "consultations") {
        setTrendSeries(consultationsTrendSeries);
        setTrendData(consultationsTrend);
      } else if (trendsFilter === "labs") {
        setTrendData(labsTrend);
        setTrendSeries(labsTrendSeries);
      } else if (trendsFilter === "prescriptions") {
        setTrendData(prescriptionsTrend);
        setTrendSeries(prescriptionsTrendSeries);
      }
    }
  }, [trendsFilter, labsTrend, consultationsTrend]);

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={ROLES.ADMIN}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 3, lg2: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-consultations")}
            amount={consultations?.total || 0}
            isLoading={isConsultationsLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconFirstAidKit
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-patients")}
            amount={patients?.total || 0}
            isLoading={isPatientsLoading}
            icon={
              <Group
                bg="blue.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconUsers
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-blue-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-earnings")}
            amount={earnings?.total || 0}
            isLoading={isEarningsLoading}
            amountPostfix="CFA"
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconMoneybag
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
            showAlertInfo={t("inaccurate-earnings")}
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12, md: 8, xl: 9 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("trends")}
                </Title>

                <Group gap={"md"}>
                  <Select
                    w={150}
                    data={[
                      { label: t("consultations"), value: "consultations" },
                      { label: t("labs"), value: "labs" },
                      { label: t("prescriptions"), value: "prescriptions" },
                    ]}
                    value={trendsFilter}
                    onChange={setTrendsFilter}
                  />
                  <NumberInput
                    w={150}
                    min={0}
                    value={trendTarget}
                    onChange={setTrendTarget}
                    placeholder={t("type-target")}
                  />
                </Group>
              </Group>

              {renderLoadingErrorData({
                isLoading:
                  isConsultationsTrendLoading ||
                  isLabsTrendLoading ||
                  isPrescriptionsTrendLoading,
                isError:
                  isConsultationsTrendError ||
                  isLabsTrendError ||
                  isPrescriptionsTrendError,
                isSuccess:
                  isConsultationsTrendSuccess ||
                  isLabsTrendSuccess ||
                  isPrescriptionsTrendSuccess,
                dataLength:
                  consultationsTrend?.length ||
                  labsTrend?.length ||
                  prescriptionsTrend?.length ||
                  0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  // <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"80%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"20%"} w={"8%"} />
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"70%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"30%"} w={"8%"} />
                    <Skeleton h={"60%"} w={"8%"} />
                  </Group>
                ),
                dataComponent: (
                  <BarChart
                    mt={"1rem"}
                    h={300}
                    data={trendData || []}
                    // yAxisLabel="Consultations"
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    referenceLines={
                      trendTarget
                        ? [
                            {
                              y: trendTarget,
                              color: "red.8",
                              label: t("target"),
                              labelPosition: "insideBottomLeft",
                            },
                          ]
                        : []
                    }
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={trendSeries || []}
                    tickLine="y"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
            <Paper
              withBorder
              p="md"
              w={{ base: "fit-content", md: "100%" }}
              miw={{ base: 290, md: "auto" }}>
              <Title order={3} fz={17} fw={500}>
                {t("billing-overview")}
              </Title>

              {renderLoadingErrorData({
                isLoading: isBillsLoading,
                isSuccess: isBillsSuccess,
                isError: isBillsError,
                dataLength: bills?.total || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={236}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  <Skeleton
                    h={236}
                    w={236}
                    mt={"1rem"}
                    radius={"100vmax"}
                    mx="auto"
                  />
                ),
                dataComponent: (
                  <PieChart
                    mt={"1rem"}
                    mx="auto"
                    withTooltip
                    size={236}
                    withLabelsLine={false}
                    labelsPosition="inside"
                    labelsType="percent"
                    withLabels
                    data={pieChartData}
                    strokeWidth={5}
                  />
                ),
              })}

              <Group justify="space-around" mt="2.4rem">
                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="orange.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("pending")}
                    </Text>
                  </Group>
                  {isBillsLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {bills?.pending || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="indigo.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("accepted")}
                    </Text>
                  </Group>
                  {isBillsLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {bills?.accepted || 0}
                    </Text>
                  )}
                </Stack>

                <Stack gap={7}>
                  <Group gap={7}>
                    <Box
                      w={7}
                      h={15}
                      bg="teal.5"
                      style={{ borderRadius: "100vmax" }}
                    />
                    <Text fz={12} c="dimmed">
                      {t("paid")}
                    </Text>
                  </Group>
                  {isBillsLoading ? (
                    <Skeleton h={15} w={40} />
                  ) : (
                    <Text fz={15} fw={500}>
                      {bills?.paid || 0}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12, md: 6, xl: 5 }}>
            <Paper
              withBorder
              p="md"
              w={{ base: "fit-content", md: "100%" }}
              mih={466}>
              <Title order={3} fz={17} fw={500}>
                {t("frequent-services")}
              </Title>

              <Table withRowBorders={false} mt={"1rem"}>
                <Table.Thead>
                  <Table.Tr
                    style={{
                      borderBottom: "1px solid var(--mantine-color-gray-3)",
                    }}>
                    <Table.Th fw={500}>{t("frequency")}</Table.Th>
                    <Table.Th fw={500}>{t("service")}</Table.Th>
                    <Table.Th fw={500} ta="right">
                      {t("earnings")} (CFA)
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                {renderLoadingErrorData({
                  isLoading: isFrequentServicesLoading,
                  isError: isFrequentServicesError,
                  isSuccess: isFrequentServicesSuccess,
                  dataLength: frequentServices?.length || 0,
                  emptyDataComponent: (
                    <Table.Tr mt={"1rem"} h={236}>
                      <Table.Td colSpan={3}>
                        <Text fz={15} c="dimmed" lh={1.5} ta={"center"}>
                          {t("empty-data")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ),
                  errorComponent: (
                    <Table.Tr mt={"1rem"} h={236}>
                      <Table.Td colSpan={3}>
                        <Text fz={15} c="dimmed" lh={1.5} ta={"center"}>
                          {t("encountered-error")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ),
                  loadingComponent: (
                    <>
                      {Array(8)
                        .fill(0)
                        .map((_, index) => (
                          <Table.Tr mt={"1rem"} key={index}>
                            <Table.Td>
                              <Skeleton h={25} w={50} />
                            </Table.Td>
                            <Table.Td>
                              <Skeleton h={25} w={180} />
                            </Table.Td>
                            <Table.Td>
                              <Skeleton h={25} w={100} ml="auto" />
                            </Table.Td>
                          </Table.Tr>
                        ))}
                    </>
                  ),
                })}
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6, xl: 7 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("top-x-performing-staff", { amount: 5 })}
                </Title>
                <Group>
                  <Select
                    value={topPerformingStaffType}
                    onChange={setTopPerformingStaffType}
                    data={[
                      {
                        label: t("doctors"),
                        value: "doctor",
                      },
                      {
                        label: t("support-staff"),
                        value: "aid",
                      },
                    ]}
                    w={150}
                    allowDeselect={false}
                  />
                  <Select
                    data={[
                      {
                        label: t("all"),
                        value: "all",
                      },
                      { label: t("consultations"), value: "consultation" },
                      { label: t("labs"), value: "lab" },
                      { label: t("prescriptions"), value: "prescription" },
                    ]}
                    value={topPerformingStaffRecord}
                    onChange={setTopPerformingStaffRecord}
                    w={150}
                    allowDeselect={false}
                  />
                </Group>
              </Group>

              {renderLoadingErrorData({
                isLoading: isTopPerformingStaffLoading,
                isError: isTopPerformingStaffError,
                isSuccess: isTopPerformingStaffSuccess,
                dataLength: topPerformingStaff?.length || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={380}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={380}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  <Stack h={380} gap={"md"} mt={"1rem"}>
                    <Skeleton h={40} w={"100%"} />
                    <Skeleton h={40} w={"50%"} />
                    <Skeleton h={40} w={"70%"} />
                    <Skeleton h={40} w={"20%"} />
                    <Skeleton h={40} w={"40%"} />
                    <Skeleton h={40} w={"50%"} />
                    <Skeleton h={40} w={"90%"} />
                  </Stack>
                ),
                dataComponent: (
                  <BarChart
                    mt={"1rem"}
                    h={380}
                    pl={"3rem"}
                    data={topPerformingStaff || []}
                    // xAxisLabel="Months"
                    // yAxisLabel="Consultations"
                    dataKey="fullName"
                    orientation="vertical"
                    gridAxis="y"
                    withLegend
                    tt={"capitalize"}
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    tooltipAnimationDuration={150}
                    // withBarValueLabel
                    series={filteredSeries}
                    tickLine="x"
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
